// src/mapPois.js
import PopupMap from "./PopupMap.svelte";

function getBlackOrWhite(color) {
  return parseInt(color.replace("#", ""), 16) > 0xffffff / 2
    ? "#000000"
    : "#ffffff";
}

function getPopup(poi, scenes) {
  if (!poi) return;
  const { title, description } = poi;
  let { sceneIds, location } = poi;
  // Parse JSON strings
  if (typeof sceneIds === "string") sceneIds = JSON.parse(sceneIds);
  if (typeof location === "string") location = JSON.parse(location);

  // convert the scene ids into linked scenes
  const linkedscenes = sceneIds?.map((sceneId) => {
    const scene = scenes?.find((scene) => scene.id === sceneId);
    return {
      id: sceneId,
      name: scene?.name,
      title: scene?.title,
    };
  });

  return {
    title,
    content: description,
    linkedscenes,
    directions: location,
  };
}

// get a darker or lighter color for hover effect (depending on darkness of color)
function getHoverColor(color) {
  const colorInt = parseInt(color.replace("#", ""), 16);
  const isDark = colorInt > 0xffffff / 2;
  const factor = isDark ? 0.9 : 1.1;
  const newColorInt = Math.round(colorInt * factor);
  return "#" + newColorInt.toString(16);
}

export function addPOIs({ map, pois, bgColor, textColor, setTooltip, scenes }) {
  bgColor = bgColor || "#000000";
  textColor = textColor || getBlackOrWhite(bgColor);
  const strokeColor = textColor;
  // just use the same color as the points for now
  const clusterColor = bgColor;
  // determine if cluster text color should be white or black based on darkness of cluster color
  const clusterTextColor = textColor;

  const geojsonSource = {
    type: "FeatureCollection",
    features: pois.map((poi) => ({
      type: "Feature",
      properties: poi,
      geometry: {
        type: "Point",
        coordinates: poi.location,
      },
    })),
  };

  map.addSource("pois", {
    type: "geojson",
    data: geojsonSource,
    cluster: true,
    clusterRadius: 50,
    clusterMaxZoom: 14,
  });

  map.addLayer({
    id: "clusters",
    type: "circle",
    source: "pois",
    filter: ["has", "point_count"],
    paint: {
      "circle-color": clusterColor,
      "circle-radius": 24,
      "circle-stroke-width": 2,
      "circle-stroke-color": clusterTextColor,
    },
  });

  map.addLayer({
    id: "cluster-count",
    type: "symbol",
    source: "pois",
    filter: ["has", "point_count"],
    layout: {
      "text-field": "{point_count_abbreviated}",
      "text-font": ["Arial Unicode MS Bold", "DIN Offc Pro Bold"],
      "text-size": 12,
    },
    paint: {
      "text-color": clusterTextColor,
    },
  });

  map.addLayer({
    id: "unclustered-point",
    type: "circle",
    source: "pois",
    filter: ["!", ["has", "point_count"]],
    paint: {
      "circle-color": bgColor,
      "circle-radius": 8,
      "circle-stroke-width": 1,
      "circle-stroke-color": strokeColor,
    },
  });

  map.on("click", "clusters", (e) => {
    const features = map.queryRenderedFeatures(e.point, {
      layers: ["clusters"],
    });
    const clusterId = features[0].properties.cluster_id;
    const clusterSource = map.getSource("pois");

    // Fetch the points within the cluster to calculate the bounds
    clusterSource.getClusterLeaves(
      clusterId,
      Infinity, // Fetch all points within the cluster
      0,
      (error, points) => {
        if (error) return;

        // Convert point coordinates to bounds
        const coordinates = points.map((point) => point.geometry.coordinates);
        const bounds = coordinates.reduce(
          (bounds, coord) => bounds.extend(coord),
          new mapboxgl.LngLatBounds(coordinates[0], coordinates[0]),
        );

        // Fit the map to the bounds
        map.fitBounds(bounds, {
          padding: 100,
          maxZoom: 17,
        });
      },
    );
  });

  map.on("mouseenter", "clusters", () => {
    map.getCanvas().style.cursor = "pointer";
  });

  map.on("mousemove", "clusters", (e) => {
    const features = map.queryRenderedFeatures(e.point, {
      layers: ["clusters"],
    });
    if (features.length) {
      map.getCanvas().style.cursor = "pointer";
    } else {
      map.getCanvas().style.cursor = "";
    }
  });

  map.on("mouseleave", "clusters", () => {
    map.getCanvas().style.cursor = "";
  });

  map.on("click", "unclustered-point", (e) => {
    console.log("poi click", e.features[0].properties);
    const popup = getPopup(e.features?.[0]?.properties, scenes);
    // if (popup) open(PopupMap, popup, popupOptions);
    const popupMap = new PopupMap({
      target: document.body,
      props: popup,
    });
  });

  map.on("mousemove", "unclustered-point", (e) => {
    const x = e.point.x;
    const y = e.point.y;
    const text = e.features[0]?.properties?.title;
    if (text) setTooltip({ x, y, text });

    // only show pointer if there is a popup
    const popup = getPopup(e.features?.[0]?.properties, scenes);
    if (popup) {
      map.getCanvas().style.cursor = "pointer";
    }
  });

  map.on("mouseleave", "unclustered-point", () => {
    map.getCanvas().style.cursor = "";
    setTooltip(null);
  });
}
